import { assign } from 'lodash';
import { LocationInfo } from '../../../components/locationCard/LocationCard';
import { convertToAnalyticsArticleSearch } from '../adapters/articleSearchAdapter';
import { AnalyticsArticle } from '../interfaces/analyticsArticle';
import { AnalyticsArticleSearch } from '../interfaces/analyticsArticleSearch';
import { AnalyticsEvent } from '../interfaces/analyticsEvent';
import { AnalyticsProject } from '../interfaces/analyticsProject';
import articleBuilder from './builders/articleBuilder';
import articleSearchBuilder from './builders/articleSearchBuilder';
import projectBuilder from './builders/projectBuilder';
import userBuilder from './builders/userBuilder';
import * as eventActions from './constants/eventActions';
import * as eventCategories from './constants/eventCategories';
import * as eventNames from './constants/eventNames';
import { ANALYTICS_FEATURES } from '../featureTypes';
import { AnalyticsUser } from '../interfaces/analyticsUser';

export function articleEvent(
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
  feature: string,
) {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_CLICK_ACTION,
    label: `${source}-${feature}`,
    source: `${source}-${feature}`,
    target: '',
  };

  return assign(
    baseEvent,
    projectBuilder.build(project),
    articleBuilder.build(article),
    userBuilder.build(user),
  );
}

export function articleAuthorAndTagEvent(
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_CLICK_ACTION,
    label: source,
    source: source,
    target: '',
  };

  return assign(
    baseEvent,
    projectBuilder.build(project),
    articleBuilder.build(article),
    userBuilder.build(user),
  );
}

export function articleSrpEvent(
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_CLICK_ACTION,
    label: source,
    source: source,
    target: '',
  };

  return assign(
    baseEvent,
    projectBuilder.build(project),
    articleBuilder.build(article),
    userBuilder.build(user),
  );
}

export const highLightEvent = (
  user: AnalyticsUser,
  highLightName: string,
  source: string,
  feature: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.HIGHLIGHT_TOPIC_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.HIGHLIGHT_TOPIC_CLICK_ACTION,
    label: `${source}-${feature}`,
    source: `${source}-${feature}`,
    target: highLightName,
  };
  return assign(userBuilder.build(user), baseEvent);
};

export const propertyTypeTabEvent = (
  user: AnalyticsUser,
  source: string,
  tabName: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.PROPERTY_TYPE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.PROPERTY_TYPE_CLICK_ACTION,
    label: source,
    source: source,
    target: tabName,
  };
  return assign(userBuilder.build(user), baseEvent);
};

export const viewMoreLinkEvent = (
  user: AnalyticsUser,
  currentSource: string,
  targetSource: string,
  feature: string,
) => {
  const baseEvent = {
    event: eventNames.ARTICLE_VIEW_ALL_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.VIEW_ALL_ARTICLES,
    label: `${currentSource}-${feature}`,
    source: `${currentSource}-${feature}`,
    target: targetSource,
  };

  return assign(baseEvent, userBuilder.build(user));
};

export const recommendedLocationCarouselArrowEvent = (
  user: AnalyticsUser,
  source: string,
  feature: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_CAROUSEL_ARROW_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_CAROUSEL_ACTION,
    label: source,
    source: `${source}-${feature}`,
    target: '',
  };
  return assign(userBuilder.build(user), baseEvent);
};

export const recommendedLocationCardEvent = (
  user: AnalyticsUser,
  locationInfo: LocationInfo,
  source: string,
  feature: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_SEARCH,
    category: eventCategories.ARTICLE_SEARCH,
    action: `${source}-${feature}-${locationInfo.index}`,
    label: '',
    source: `${source}-${feature}-${locationInfo.index}`,
    target: feature,
  };
  const analyticsArticleSearch: AnalyticsArticleSearch =
    convertToAnalyticsArticleSearch(locationInfo);
  return assign(
    userBuilder.build(user),
    baseEvent,
    articleSearchBuilder.build(analyticsArticleSearch),
  );
};

export const articleFeedShowMoreEvent = (
  user: AnalyticsUser,
  source: string,
  feature: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_FEED_SHOW_MORE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_FEED_SHOW_MORE_CLICK_ACTION,
    label: `${source}-${feature}`,
    source: `${source}-${feature}`,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const articleAuthorAndTagFeedShowMoreEvent = (
  user: AnalyticsUser,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_FEED_SHOW_MORE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_FEED_SHOW_MORE_CLICK_ACTION,
    label: source,
    source: source,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const articleSrpFeedShowMoreEvent = (
  user: AnalyticsUser,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_FEED_SHOW_MORE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_FEED_SHOW_MORE_CLICK_ACTION,
    label: source,
    source: source,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const articleFeedTabEvent = (
  user: AnalyticsUser,
  source: string,
  feature: string,
  selectedArticleFeedTab: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_FEED_TAB_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_FEED_TAB_CLICK_ACTION,
    label: `${source}-${feature}`,
    source: `${source}-${feature}`,
    target: selectedArticleFeedTab,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const articleAuthorAndTagFeedTabEvent = (
  user: AnalyticsUser,
  source: string,
  selectedArticleFeedTab: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_FEED_TAB_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_FEED_TAB_CLICK_ACTION,
    label: source,
    source: source,
    target: selectedArticleFeedTab,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const articleSrpFeedTabEvent = (
  user: AnalyticsUser,
  source: string,
  selectedArticleFeedTab: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_FEED_TAB_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_FEED_TAB_CLICK_ACTION,
    label: source,
    source: source,
    target: selectedArticleFeedTab,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const editorsPicksCarouselEvent = (
  user: AnalyticsUser,
  source: string,
  feature: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.EDITORS_PICKS_CAROUSEL_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.EDITORS_PICKS_CAROUSEL_CLICK_ACTION,
    label: `${source}-${feature}`,
    source: `${source}-${feature}`,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const inPageSearchButtonEvent = (
  user: AnalyticsUser,
  source: string,
  locationType: string,
  locationName: string,
  selectedMaxValue: string,
  selectedMinValue: string,
  feature: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_SEARCH,
    category: eventCategories.ARTICLE_SEARCH,
    action: `${source}-${feature}`,
    label: '',
    source: `${source}-${feature}`,
    target: eventActions.LOCATION_SEARCH_ACTION,
  };

  const articleSearchObject = articleSearchBuilder.build({
    locationName,
    locationType,
    selectedMaxValue,
    selectedMinValue,
  });

  return assign(baseEvent, userBuilder.build(user), articleSearchObject);
};

export const freeTextSearchEvent = (
  user: AnalyticsUser,
  source: string,
  keyword: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_SEARCH,
    category: eventCategories.ARTICLE_SEARCH,
    action: `${source}-${eventActions.FREE_TEXT_SEARCH_ACTION}`,
    label: keyword,
    source: `${source}-${eventActions.FREE_TEXT_SEARCH_ACTION}`,
    target: eventActions.FREE_TEXT_SEARCH_ACTION,
  };

  return assign(
    userBuilder.build(user),
    baseEvent,
    articleSearchBuilder.build({ keyword }),
  );
};

export const articleCarouselArrowEvent = (
  user: AnalyticsUser,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_CAROUSEL_ARROW_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_CAROUSEL_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const articleShowMoreEvent = (user: AnalyticsUser, source: string) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_SHOW_MORE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_MORE_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const selectFromSearchSuggestionEvent = (
  user: AnalyticsUser,
  source: string,
  term: string,
  projectName: string,
  locationName: string,
  locationType: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_SEARCH,
    category: eventCategories.ARTICLE_SEARCH,
    action: `${source}-${ANALYTICS_FEATURES.AUTO_SUGGEST}`,
    label: '',
    source: `${source}-${ANALYTICS_FEATURES.AUTO_SUGGEST}`,
    target: term,
  };
  return assign(
    userBuilder.build(user),
    baseEvent,
    articleSearchBuilder.build({ locationName, locationType, projectName }),
  );
};

export const projectSubscribeButtonEvent = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.PROJECT_SUBSCRIBE_BUTTON_CLICK,
    category: eventCategories.PROJECT_INTERACTIONS,
    action: eventActions.PROJECT_SUBSCRIBE_CONTACT_CLICK_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(
    userBuilder.build(user),
    projectBuilder.build(project),
    baseEvent,
  );
};
export function articleReadMoreEvent(
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_READ_MORE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_READ_MORE_ACTION,
    label: `${source}`,
    source: `${source}`,
    target: '',
  };

  return assign(
    baseEvent,
    projectBuilder.build(project),
    articleBuilder.build(article),
    userBuilder.build(user),
  );
}

export const articleSubscribeButtonEvent = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_SUBSCRIBE_BUTTON_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_SUBSCRIBE_CLICK_CONTACT_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(
    userBuilder.build(user),
    articleBuilder.build(article),
    projectBuilder.build(project),
    baseEvent,
  );
};

export const projectEvent = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.RELATED_PROJECT_CLICK,
    category: eventCategories.PROJECT_INTERACTIONS,
    action: eventActions.PROJECT_CLICK_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(
    userBuilder.build(user),
    projectBuilder.build(project),
    baseEvent,
  );
};

export const articleSubmittedCommentEvent = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_SUBMITTED_COMMENT,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.COMMENT_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(
    userBuilder.build(user),
    articleBuilder.build(article),
    projectBuilder.build(project),
    baseEvent,
  );
};

export const GDPREvent = (
  user: AnalyticsUser,
  action: string,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.GDPR_CLOSE,
    category: eventCategories.GDPR_POPUP_MESSAGE,
    action,
    label: '',
    source,
    target: '',
  };
  return assign(baseEvent, userBuilder.build(user));
};

export const projectSubmittedCommentEvent = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_SUBMITTED_COMMENT,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.COMMENT_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(
    userBuilder.build(user),
    projectBuilder.build(project),
    baseEvent,
  );
};

export const articleArrowEvent = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_CLICK_ON_ARROW,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.GALLERY_NEXT_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(
    userBuilder.build(user),
    articleBuilder.build(article),
    baseEvent,
  );
};

export const loginEvent = (user: AnalyticsUser, source: string) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.LOGIN_BUTTON_ClICK,
    category: eventCategories.USER,
    action: eventActions.LOGIN_BUTTON_CLICK_ACTION,
    label: '',
    source,
    target: '',
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const registerEvent = (user: AnalyticsUser, source: string) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.REGISTER_BUTTON_CLICK,
    category: eventCategories.USER,
    action: eventActions.REGISTER_BUTTON_CLICK_ACTION,
    label: '',
    source,
    target: '',
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const articleThumbnailEvent = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  source: string,
  target: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_CLICK_ON_THUMBNAIL,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.GALLERY_THUMBNAIL_ACTION,
    label: source,
    source,
    target,
  };

  return assign(
    userBuilder.build(user),
    articleBuilder.build(article),
    baseEvent,
  );
};

export const articleTypeTabEvent = (
  user: AnalyticsUser,
  source: string,
  target: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_TYPE_TAB_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_TYPE_CHANGE_ACTION,
    label: source,
    source,
    target,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const relatedArticleEvent = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  project: AnalyticsProject,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.RELATED_ARTICLE_CLICK,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_CLICK_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(
    userBuilder.build(user),
    articleBuilder.build(article),
    projectBuilder.build(project),
    baseEvent,
  );
};

export const relatedProjectEvent = (
  user: AnalyticsUser,
  project: AnalyticsProject,
  source: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.RELATED_PROJECT_CLICK,
    category: eventCategories.PROJECT_INTERACTIONS,
    action: eventActions.PROJECT_CLICK_ACTION,
    label: source,
    source,
    target: '',
  };

  return assign(
    userBuilder.build(user),
    projectBuilder.build(project),
    baseEvent,
  );
};

export const navigationItemEvent = (
  user: AnalyticsUser,
  source: string,
  targetSource: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.NAVIGATION_ITEM_CLICK,
    category: eventCategories.NAVIGATION_ITEM_CLICK,
    action: eventActions.NAVIGATION_ITEM_CLICK,
    label: source,
    source,
    target: targetSource,
  };

  return assign(userBuilder.build(user), baseEvent);
};

export const inPageNavigationEvent = (
  user: AnalyticsUser,
  article: AnalyticsArticle,
  source: string,
  target: string,
) => {
  const baseEvent: AnalyticsEvent = {
    event: eventNames.ARTICLE_PAGENAVIGATION,
    category: eventCategories.ARTICLE_INTERACTIONS,
    action: eventActions.ARTICLE_PAGE_NAVIGATION,
    label: source,
    source,
    target,
  };

  return assign(
    userBuilder.build(user),
    articleBuilder.build(article),
    baseEvent,
  );
};
